import React from 'react';
import { useInView } from 'react-intersection-observer'

import useForceSimulation from './useForceSimulation.js'
import PopulationDynamics from './PopulationDynamics'

const PopulationSpreadSim = ({
  width = 400,
  height = 400,
  graph, 
  simulator, 
  initializeSimulationState,
  statesList,
  linkRenderer, 
  nodesRenderer,
  framesPerDay = 4,
  zoom = 1, 
  controls = {}, 
  children,
}) => {
  const canvasRef = React.useRef()
  const linksCanvasRef = React.useRef()
  const controlsRef = React.useRef()
  const resetRef = React.useRef()
  const inViewRef = React.useRef()
  const fullViewRef = React.useRef()

  const [wrapperRef, inView, entry] = useInView({
    /* Optional options */
    threshold: 0,
  })

  const [canvasWrapperRef, inFullView] = useInView({
    threshold: 0.9
  })

  const [notInitialized, setNotInitialized] = React.useState(true)
  const [simulationState, setSimulationState] = React.useState(initializeSimulationState(graph))
  const [isSimulating, setIsSimulating] = React.useState(false)

  const toggleSimulation = () => {
    setIsSimulating(!isSimulating)
  }

  const resetSimulation = () => {
    setSimulationState(initializeSimulationState(graph))
    setIsSimulating(false)
    resetRef.current = true
  }

  const actualWidth = (width < window.innerWidth) ? width : window.innerWidth;
  const actualHeight = (width < window.innerWidth) ? height : (height / width * window.innerWidth)

  let changeDays = []
  if (controls.linksInPlay && controls.linksInPlay.changeDays) {
    changeDays = controls.linksInPlay.changeDays
  }

  useForceSimulation(
    graph.nodes, 
    graph.links, 
    [isSimulating, simulationState, inView, inFullView, notInitialized], // Controls
    width,
    height,
    zoom, 
    (nodes, links, hash, frame, isForceLayoutTicking) => {
      const canvas = canvasRef.current
      const context = canvas.getContext('2d')

      const linksCanvas = linksCanvasRef.current
      const linksContext = linksCanvas.getContext('2d')

      const scaleFactor = window.devicePixelRatio * zoom;
      linksContext.scale(scaleFactor, scaleFactor);
      context.scale(scaleFactor, scaleFactor);

      // Core of the Simulation
      let day = simulationState.history.length
      
      setSimulationState((prevSimState) => {
        const controlsChanged = (controls !== controlsRef.current)
        const viewChanged = (inView !== inViewRef.current)

        if (inView) {
          // Rendering Code
          let resurgenceLinksRepaint = (changeDays.indexOf(day) >= 0 && ((frame % framesPerDay) === 0))

          if (viewChanged || isForceLayoutTicking || controlsChanged || resetRef.current === true || resurgenceLinksRepaint) {
            linksContext.clearRect(0, 0, width / zoom, height / zoom);
            if (linkRenderer) {
              linkRenderer(linksContext, links, controls, day)
            }
          }

          if (viewChanged || isSimulating || isForceLayoutTicking || resetRef.current === true) {
            context.clearRect(0, 0, width / zoom, height / zoom);
            if (nodesRenderer) {
              nodesRenderer(context, nodes, prevSimState.population, day, frame, framesPerDay, controls)
            }
          }
        }

        let nextSimulationState;
        if (frame % framesPerDay === 0 && isSimulating) { // update the status every 60 frame
          nextSimulationState = simulator(nodes, hash, prevSimState, controls)
        } else {
          nextSimulationState = prevSimState
        }

        controlsRef.current = controls
        resetRef.current = false

        return nextSimulationState
      })

      setIsSimulating((s) => {
        // Start if scrolled into view for the first time
        const viewChanged = (inFullView !== fullViewRef.current)

        // Stop When reached the whole population
        const latest = simulationState.history[simulationState.history.length - 1]

        if (viewChanged && inFullView && notInitialized) {
          setNotInitialized(false)
          return true
        }

        return s && (latest.INFECTED > 0);
      })

      fullViewRef.current = inFullView
      inViewRef.current = inView

      // Necessary to prevent context.scale from stacking
      linksContext.setTransform(1, 0, 0, 1, 0, 0);
      context.setTransform(1, 0, 0, 1, 0, 0);
    }
  )

  let scaleFactor = window.devicePixelRatio;

  return <div className="simulation-wrapper" ref={wrapperRef}>
    <div>
      {children}
    </div>
    <div className="canvas-stack" ref={canvasWrapperRef}>
      <canvas className="links-canvas" ref={linksCanvasRef} width={width * scaleFactor} height={height * scaleFactor}
        style={{
          width: `${actualWidth}px`,
          height: `${actualHeight}px`
        }}
      />
      <canvas className="nodes-canvas" ref={canvasRef} width={width * scaleFactor} height={height * scaleFactor}
        style={{
          width: `${actualWidth}px`,
          height: `${actualHeight}px`
        }}
      />
    </div>
    <div className="simulation-stats" ref={canvasWrapperRef}>
      <PopulationDynamics 
        populationTimeSeries={simulationState.history} 
        statesList={statesList}
        changeDays={changeDays}
      />
    </div>
    <div className="simulation-controls">
      <button onClick={toggleSimulation}>{(isSimulating) ? "Stop" : "Start"}</button>
      <button onClick={resetSimulation}>Reset</button>
    </div>
  </div>;
};

export default PopulationSpreadSim;